"use client"

import { useEffect, useRef, type ReactNode, type Ref } from "react"
import { ContactConsentTrackingEvents } from "@/app/constants/funnelTracking"
import { IConsentPartnerInfo } from "@/app/endpoints/healthComparatorEndpoints"
import { formatPartnerList } from "@/app/utils/health/consent-partners"
import type { ContactConsentProduct } from "@/app/constants/contact-consent"

export type { ContactConsentProduct }

export const CONTACT_CONSENT_ERROR_MESSAGE = "Cochez cette case pour accéder à vos résultats."

interface ContactConsentCopy {
    title: string
    intro: string
    phonePill: string
    emailPill: string
}

const EMAIL_TRACKING_PILL = "Mesure d'ouverture des emails"

// Bump CONTACT_CONSENT_WORDING_VERSIONS (constants/contact-consent) with every change of these texts
const COPY: Record<ContactConsentProduct, ContactConsentCopy> = {
    loan: {
        title: "J'accepte d'être recontacté(e) par un expert Réassurez-moi par téléphone et email, avec suivi de l'ouverture et des clics.",
        intro: "Un conseiller vous appelle pour étudier votre projet d'assurance emprunteur et vous envoie par email vos offres et le suivi de votre dossier. Un pixel mesure l'ouverture et les clics de nos emails : vous pouvez vous y opposer via le lien en bas de chaque email. Réassurez-moi – ORIAS n° 13010052.",
        phonePill: "Appel d'un expert dédié",
        emailPill: "Offres et garanties par email",
    },
    health: {
        title: "J'accepte d'être recontacté(e) par les partenaires de Réassurez-moi, par téléphone et email.",
        intro: "Le partenaire de votre choix vous appelle pour finaliser votre devis de mutuelle santé et vous envoie par email vos offres et le suivi de votre dossier. Un pixel mesure l'ouverture et les clics de nos emails : vous pouvez vous y opposer via le lien en bas de chaque email.",
        phonePill: "Appel du partenaire choisi",
        emailPill: "Offres et garanties par email",
    },
}

// The partner list depends on the persona category, resolved when the zipcode step is validated; no list, no sentence
const healthPartnersSentence = (partners: IConsentPartnerInfo[]) =>
    partners.length > 0
        ? ` Sans choix de partenaire à l'étape suivante, l'un des partenaires suivants pourra vous contacter selon votre profil : ${formatPartnerList(partners)}.`
        : ""

const ICON_PROPS = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "#2D3838",
    strokeWidth: 2.2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
} as const

function PhoneIcon() {
    return (
        <svg {...ICON_PROPS} className="size-[13px] shrink-0 md:size-[14px]">
            <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
        </svg>
    )
}

function MailIcon() {
    return (
        <svg {...ICON_PROPS} className="size-[13px] shrink-0 md:size-[14px]">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-10 7L2 7" />
        </svg>
    )
}

function EyeIcon() {
    return (
        <svg {...ICON_PROPS} className="size-[13px] shrink-0 md:size-[14px]">
            <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    )
}

function ChannelPill({ icon, children }: { icon: ReactNode; children: ReactNode }) {
    return (
        <span className="inline-flex items-center gap-[6px] rounded-full bg-neutral px-[9px] py-[5px] text-[12px] font-bold text-pale md:px-[11px] md:py-[6px] md:text-[13px]">
            {icon}
            {children}
        </span>
    )
}

interface SingleContactConsentCheckboxProps {
    product: ContactConsentProduct
    checked: boolean
    onChange: (checked: boolean) => void
    error?: boolean
    partners?: IConsentPartnerInfo[]
    trackEvent?: (event: string, params: Record<string, unknown>) => void
    id?: string
    ref?: Ref<HTMLInputElement>
}

// One mandatory box grants both channels (phone + email) and the tracking of our emails on the AE/MS contact-details step
export default function SingleContactConsentCheckbox({
    product,
    checked,
    onChange,
    error = false,
    partners = [],
    trackEvent,
    id = "accept_contact",
    ref,
}: SingleContactConsentCheckboxProps) {
    const copy = COPY[product]
    const intro = product === "health" ? copy.intro + healthPartnersSentence(partners) : copy.intro
    const titleId = `${id}-title`
    const introId = `${id}-intro`
    const errorId = `${id}-error`

    const trackEventRef = useRef(trackEvent)
    useEffect(() => {
        trackEventRef.current = trackEvent
    })

    useEffect(() => {
        if (error) trackEventRef.current?.(ContactConsentTrackingEvents.errorShown, { product })
    }, [error, product])


    return (
        <div
            data-testid="single-contact-consent"
            className={`flex flex-col gap-3 rounded-[14px] bg-white p-[14px] md:gap-[14px] md:px-5 md:py-[18px] ${
                error ? "border-[1.5px] border-rm-orange" : "border border-[#EFE6E2]"
            }`}
        >
            {/* the whole card is the label; aria-labelledby keeps the accessible name to the title alone */}
            <label htmlFor={id} className="m-0 flex cursor-pointer items-start gap-3 md:gap-[14px]">
                <span className="relative mt-px size-[22px] shrink-0">
                    <input
                        ref={ref}
                        type="checkbox"
                        id={id}
                        name={id}
                        checked={checked}
                        onChange={(e) => onChange(e.target.checked)}
                        aria-required="true"
                        aria-invalid={error || undefined}
                        aria-labelledby={titleId}
                        aria-describedby={error ? `${introId} ${errorId}` : introId}
                        className="peer absolute inset-0 z-10 m-0 size-full cursor-pointer opacity-0"
                    />
                    <span
                        aria-hidden="true"
                        className={`pointer-events-none absolute inset-0 flex items-center justify-center rounded-md peer-focus-visible:ring-2 peer-focus-visible:ring-rm-blue/40 peer-focus-visible:ring-offset-1 ${
                            checked ? "bg-rm-blue" : `border-2 bg-white ${error ? "border-rm-orange" : "border-[#C9CDD6]"}`
                        }`}
                    >
                        {checked && (
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#fff"
                                strokeWidth={3.2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="size-[14px]"
                            >
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                        )}
                    </span>
                </span>

                <span className="flex min-w-0 flex-1 flex-col gap-2 md:gap-[10px]">
                    <span className="flex items-start justify-between gap-[10px]">
                        <span id={titleId} className="text-[14px] font-bold leading-[1.4] text-pale md:text-[15px]">
                            {copy.title} <span className="text-rm-orange">*</span>
                        </span>
                        <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-[#FDEDE7] px-[9px] py-[3px] text-[11px] font-extrabold uppercase tracking-[.04em] text-rm-orange">
                            Obligatoire
                        </span>
                    </span>
                    <span id={introId} className="text-[13px] leading-[1.45] text-[#71717A] md:text-[14px] md:leading-[1.5]">
                        {intro}
                    </span>
                    <span className="flex flex-wrap gap-2">
                        <ChannelPill icon={<PhoneIcon />}>{copy.phonePill}</ChannelPill>
                        <ChannelPill icon={<MailIcon />}>{copy.emailPill}</ChannelPill>
                        <ChannelPill icon={<EyeIcon />}>{EMAIL_TRACKING_PILL}</ChannelPill>
                    </span>
                </span>
            </label>

            <div
                id={errorId}
                aria-live="polite"
                data-testid="single-contact-consent-error"
                className={error ? "flex items-center gap-2 text-[13px] font-bold text-rm-orange" : "sr-only"}
            >
                {error && (
                    <>
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#E8531C"
                            strokeWidth={2.2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            className="size-[15px] shrink-0"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 8v4M12 16h.01" />
                        </svg>
                        {CONTACT_CONSENT_ERROR_MESSAGE}
                    </>
                )}
            </div>
        </div>
    )
}
